import { useTranslation } from 'react-i18next';
import type { GiftProduct, GiftProductId } from '../../data/giftProducts';
import { Icon } from '../header/draft/Icon';
import { PaymentCardGlassShell } from './PaymentCardGlassShell';

const GIFT_CARD_VARIANT: Record<GiftProductId, string> = {
  '100': 'payment-card-glass--gift-100',
  '365': 'payment-card-glass--gift-365',
  '1000': 'payment-card-glass--gift-1000',
  custom: 'payment-card-glass--gift-custom',
};

const PRODUCT_I18N_KEY: Record<Exclude<GiftProductId, 'custom'>, string> = {
  '100': 'gift.products.d100',
  '365': 'gift.products.d365',
  '1000': 'gift.products.d1000',
};

export type OwnedCouponStatus = '등록됨' | '이용 중';

type GiftProductCardProps = {
  product: GiftProduct;
  ownedStatus?: OwnedCouponStatus;
  ownedUsagePeriod?: string;
  ownedDaysRemaining?: number;
  ownedUseDisabled?: boolean;
  onUse?: () => void;
  onUseBlocked?: () => void;
};

export function GiftProductCard({
  product,
  ownedStatus,
  ownedUsagePeriod,
  ownedDaysRemaining,
  ownedUseDisabled = false,
  onUse,
  onUseBlocked,
}: GiftProductCardProps) {
  const { t } = useTranslation();
  const variant = GIFT_CARD_VARIANT[product.id];
  const isCustom = product.id === 'custom' || product.isCustom;
  const title = isCustom
    ? product.title ||
      (product.days > 0
        ? t('gift.products.custom.title', { count: product.days })
        : t('gift.products.custom.titleFallback'))
    : t(`${PRODUCT_I18N_KEY[product.id as Exclude<GiftProductId, 'custom'>]}.title`, {
        defaultValue: product.title,
      });
  const displayDays = product.days > 0 ? product.days : '—';
  const isOwned = ownedStatus != null;
  const isPopular = !isOwned && product.id === '365';
  const isActiveOwned = isOwned && ownedStatus === '이용 중';
  const showAnimatedBorder = isPopular || isActiveOwned;
  const canUse = ownedStatus === '등록됨';
  const statusLabel =
    ownedStatus === '등록됨' ? t('gift.pendingUse') : ownedStatus === '이용 중' ? t('gift.inUse') : '';

  return (
    <PaymentCardGlassShell
      className={`payment-card-visual--gift w-full${showAnimatedBorder ? ' payment-card-visual--gift-popular' : ''}`}
      cardClassName={`payment-card-glass--gift ${variant}${showAnimatedBorder ? ' payment-card-glass--gift-popular' : ''}${isActiveOwned ? ' payment-card-glass--gift-owned-active' : ''}`}
      maxTiltX={3}
      maxTiltY={2.5}
      whiteAnimatedBorder={showAnimatedBorder}
    >
      <div className={`gift-product-card__inner gift-product-card__inner--${product.id} h-full min-h-0`}>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {!isOwned && (
              <span className="gift-product-card__badge gift-product-card__badge--gift shrink-0">
                <Icon name="redeem" className="gift-product-card__badge-icon" />
                {t('common.gift')}
              </span>
            )}
            {isPopular && (
              <span className="gift-product-card__popular-badge shrink-0">
                <Icon name="local_fire_department" filled className="gift-product-card__badge-icon" />
                {t('common.popular')}
              </span>
            )}
            {isOwned && (
              <span className="gift-product-card__badge gift-product-card__badge--coupon-status shrink-0">
                <Icon
                  name={ownedStatus === '등록됨' ? 'schedule' : 'verified'}
                  className="text-base"
                  filled={ownedStatus === '이용 중'}
                />
                {statusLabel}
              </span>
            )}
          </div>
          <span className="gift-product-card__days font-headline text-xl font-black tabular-nums leading-none shrink-0">
            {displayDays}
            {product.days > 0 && (
              <span className="gift-product-card__days-unit text-sm font-bold ml-0.5">{t('gift.daysUnit')}</span>
            )}
          </span>
        </div>

        <div className="min-w-0 mt-2 sm:mt-2.5">
          <h3 className="font-headline text-xl sm:text-2xl font-black text-[#333] leading-snug tracking-[-0.04em] line-clamp-2">
            {title}
          </h3>
        </div>

        {isOwned ? (
          canUse ? (
            <button
              type="button"
              aria-disabled={ownedUseDisabled}
              onClick={() => (ownedUseDisabled ? onUseBlocked?.() : onUse?.())}
              className={`gift-product-card__action mt-auto w-full h-11 flex items-center justify-center rounded-full border-0 px-5 text-sm font-headline font-semibold transition-colors ${
                ownedUseDisabled
                  ? 'gift-product-card__action--muted cursor-not-allowed shadow-none'
                  : 'bg-primary text-white shadow-[0_0_20px_rgba(107,132,255,0.35)] hover:bg-primary/90 active:bg-primary/85'
              }`}
            >
              <span className="block leading-none translate-y-px">{t('gift.use')}</span>
            </button>
          ) : (
            <div className="gift-product-card__usage-period mt-auto pt-1 flex items-end justify-between gap-3 w-full min-w-0">
              <span className="text-sm sm:text-[15px] font-semibold text-[#333] leading-snug tracking-[-0.02em] tabular-nums min-w-0">
                {ownedUsagePeriod ?? '—'}
              </span>
              {typeof ownedDaysRemaining === 'number' && Number.isFinite(ownedDaysRemaining) && (
                <span className="shrink-0 text-sm sm:text-[15px] font-bold text-[#333] tabular-nums whitespace-nowrap">
                  {t('gift.daysRemaining', { count: ownedDaysRemaining })}
                </span>
              )}
            </div>
          )
        ) : (
          <p className="gift-product-card__price mt-auto pt-1 font-headline text-2xl sm:text-3xl font-black tabular-nums leading-none tracking-[-0.06em]">
            {product.priceLabel}
          </p>
        )}
      </div>
    </PaymentCardGlassShell>
  );
}
